import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { FormikProps } from "formik";
import { FILLED_LOCKED_PICKER_SX } from "@/constants/scheduling";
import CustomDatePicker from "@/components/CustomDatePicker";
import CustomTimePicker from "@/components/CustomTimePicker";
import ErrorHelper from "@/components/ErrorWrapper";
import StyledDialog from "@/components/StyledDialog";
import useDisclosure from "@/hooks/use-disclosure";
import { formatTime } from "@/utils/date-time-utility";
import FormSection from "@/components/FormSection";
import Text from "@/components/Text";
import { RecurringConfig } from "@/components/app/provider/scheduling/RecurringConfig";
import {
  GROUP_FIELDS_LABEL,
  GROUP_FIELDS_PLACEHOLDER,
  GROUP_FIELDS_SECTION,
  GROUP_SLOT_LABEL,
  SLOT_PICKER_SX,
  SLOT_TIME_FORMAT,
  VISIT_MODE,
} from "../group-visit-constant";
import { GroupFieldsValues, TherapistSlot } from "../group-visit-types";
import { toDurationMinutes } from "../group-visit-utils";
import { TherapistSlotSearch } from "../hooks/use-therapist-slots";
import GroupSessionFields from "./GroupSessionFields";
import GroupSlotPicker from "./GroupSlotPicker";

export type GroupScheduleFieldsProps = {
  formik: FormikProps<GroupFieldsValues>;
  /** Therapists whose shared availability is offered as slots. */
  providerIds: string[];
  disabled?: boolean;
};

/** Date & Time section of the group visit scheduling fields. */
const GroupScheduleFields = ({
  formik,
  providerIds,
  disabled,
}: GroupScheduleFieldsProps) => {
  const { values, errors, touched, submitCount, setFieldValue } = formik;
  const slotDialog = useDisclosure();

  const handleRecurringChange = (checked: boolean) =>
    setFieldValue("isRecurring", checked);

  const handleDateChange = (date: Dayjs | null) => {
    setFieldValue("startDate", date);
    clearSlot();
  };

  const clearSlot = () => {
    setFieldValue("startTime", null);
    setFieldValue("endTime", null);
  };

  const handleSlotSelect = (slot: TherapistSlot) => {
    setFieldValue("startDate", dayjs(slot.startTime));
    setFieldValue("startTime", slot.startTime);
    setFieldValue("endTime", slot.endTime);
    if (values.endDate?.isBefore(dayjs(slot.startTime), "day")) {
      setFieldValue("endDate", null);
    }
    slotDialog.close();
  };

  const slotSearch: TherapistSlotSearch = {
    groupVisitId: values.groupId ?? undefined,
    providerIds,
    availabilityMode: values.mode,
    locationUuid: values.locationId?.value,
    duration: toDurationMinutes(values.sessionDurationMinutes),
  };
  const canViewAvailability =
    providerIds.length > 0 &&
    (values.mode !== VISIT_MODE.IN_PERSON || !!values.locationId);

  return (
    <>
      <FormSection title={GROUP_FIELDS_SECTION.DATE_TIME}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <GroupSessionFields
              formik={formik}
              disabled={disabled}
              onAvailabilityChange={clearSlot}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <Box sx={{ width: SLOT_PICKER_SX.fieldWidth }}>
              <ErrorHelper
                error={
                  (touched.startTime || submitCount > 0) &&
                  (errors.startTime as string)
                }
              >
                <Tooltip
                  title={
                    canViewAvailability
                      ? ""
                      : GROUP_SLOT_LABEL.completeVisitFirst
                  }
                >
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                    }}
                  >
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={disabled || !canViewAvailability}
                      sx={{ height: SLOT_PICKER_SX.buttonHeight }}
                      onClick={slotDialog.open}
                    >
                      {GROUP_SLOT_LABEL.viewAvailability}
                    </Button>
                  </Box>
                </Tooltip>
              </ErrorHelper>
            </Box>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <CustomDatePicker
              disabled
              flexprops={{
                sx: values.startTime ? FILLED_LOCKED_PICKER_SX : undefined,
              }}
              label={GROUP_SLOT_LABEL.date}
              value={values.startTime ? values.startDate : null}
              placeholder={GROUP_FIELDS_PLACEHOLDER.date}
              onChange={() => undefined}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <CustomTimePicker
              disabled
              flexProps={{
                sx: values.startTime ? FILLED_LOCKED_PICKER_SX : undefined,
              }}
              label={GROUP_SLOT_LABEL.time}
              valueFormatter={SLOT_TIME_FORMAT}
              value={values.startTime ? formatTime(values.startTime) : null}
              onChange={() => undefined}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <FormControlLabel
              name="isRecurring"
              control={<Checkbox />}
              label={
                <Text fontWeight={500}>
                  {GROUP_FIELDS_LABEL.recurringSchedule}
                </Text>
              }
              checked={values.isRecurring}
              disabled={disabled}
              onChange={(_, checked) => handleRecurringChange(checked)}
            />
          </Grid>

          {values.isRecurring && <RecurringConfig formik={formik} required />}
        </Grid>
      </FormSection>

      <StyledDialog open={slotDialog.opened} onClose={slotDialog.close}>
        <GroupSlotPicker
          search={slotSearch}
          selectedDate={values.startDate}
          selectedStartTime={values.startTime}
          onDateChange={handleDateChange}
          onSlotSelect={handleSlotSelect}
        />
      </StyledDialog>
    </>
  );
};

export default GroupScheduleFields;
